import env from '../../../core/config/env'
import { linkWhatsApp } from '../../../core/functions/phoneUtils'
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material'
import { IDataTableColumn } from '../../../components/Tables/DataTable'
import DataTableCellTypography from '../../../components/Typographys/DataTableCellTypography'
import { checkoutOrderColumnsApproved } from './CheckoutOrderColumns/CheckoutOrderColumnsApproved'
import { checkoutOrderColumnsOpened } from './CheckoutOrderColumns/CheckoutOrderColumnsOpened'
import Hosts from '../../../core/functions/hosts'
import Convertions from '../../../core/functions/convertions'
import { HtmlTooltip } from '../../../components/Tooltip/HtmlTooltip'
import NumberFunctions from '../../../core/functions/NumberFunctions'
import MethodTypeSmallIcon from '../../../components/Checkout/Icons/MethodTypeSmallIcon'
import { CheckoutOrderColumnsAbandoned } from './CheckoutOrderColumns/CheckoutOrderColumnsAbandoned'
import OrderFunctions from '../../../core/functions/OrderFunctions'
import {
  TCheckoutOrder,
  TCheckoutOrderEmailStatus,
  TItemTypeSaleEnum,
} from '../../../core/types/CheckoutOrder'
import { TLostSale } from '../../../core/types/LostSale'
import GetAppIcon from '@mui/icons-material/GetApp'
import OrderController from '../../../core/controllers/OrderController'
import { IconCopy } from '../../../core/constants/IconConsts'

export const CHECKOUT_TAB_APPROVED = 0
export const CHECKOUT_TAB_ALL = 1
export const CHECKOUT_TAB_OPENED = 2
export const CHECKOUT_TAB_ABANDONED = 3
export const CHECKOUT_TAB_CANCELED = 4
export const CHECKOUT_TAB_RECONCILIATION = 5

export interface ICheckoutOrderDetailsProp {
  checkoutOrder?: TCheckoutOrder
  open: boolean
  isOpened: boolean
  handleClose: () => void
  setOpen: (value: boolean) => void
  setSuccess: (value: boolean) => void
  setError: (value: string) => void
  execute: () => void
  setCopied: (value: boolean) => void
}

export const checkoutOrderStatusColorMap: Record<
  string,
  { backgroundColor: string; color: string }
> = {
  pending: {
    backgroundColor: '#FBF3E2',
    color: '#887100',
  },
  failed: {
    backgroundColor: '#BD1C1C29',
    color: '#D4362C',
  },
  paid: {
    backgroundColor: 'rgba(28, 189, 63, 0.16)',
    color: 'rgba(0, 157, 35, 1)',
  },
  default: {
    backgroundColor: '#FBF3E2',
    color: '#887100',
  },
  warning: {
    backgroundColor: '#FBF3E2',
    color: '#887100',
  },
  chargedback: {
    backgroundColor: '#BD1C1C29',
    color: '#D4362C',
  },
  refund_claimed: {
    backgroundColor: '#BD1C1C29',
    color: '#D4362C',
  },
}

export const checkoutOrderEmailStatusColorMap: Record<
  TCheckoutOrderEmailStatus,
  { label: string; backgroundColor: string; color: string }
> = {
  [TCheckoutOrderEmailStatus.Sent]: {
    label: 'Enviado',
    backgroundColor: '#FBF3E2',
    color: '#887100',
  },
  [TCheckoutOrderEmailStatus.Failed]: {
    label: 'Falhou',
    backgroundColor: '#BD1C1C29',
    color: '#D4362C',
  },
  [TCheckoutOrderEmailStatus.Opened]: {
    label: 'Aberto',
    backgroundColor: 'rgba(28, 189, 63, 0.16)',
    color: 'rgba(0, 157, 35, 1)',
  },
}

interface ICheckoutOrderOpenedDataTableColumnsProps {
  setCopied: (value: boolean) => void
  onDownloadBoleto: (value: string, chargeId: string) => Promise<void>
  onCopyPix: (value: string, chargeId: string) => Promise<void>
  execute: () => void
}

export const CheckoutOrderDataTableColumns = ({
  setCopied,
  onDownloadBoleto,
  onCopyPix,
  execute,
}: ICheckoutOrderOpenedDataTableColumnsProps) => {
  const columns = {
    ['approved']: checkoutOrderColumnsApproved({ execute }),
    ['all']: checkoutOrderColumnsApproved({ execute }),
    ['opened']: checkoutOrderColumnsOpened(
      setCopied,
      onDownloadBoleto,
      onCopyPix,
      execute
    ),
    ['abandoned']: CheckoutOrderColumnsAbandoned(setCopied, execute),
    ['canceled']: checkoutOrderColumnsApproved({ execute }),
  }

  return columns
}

export const checkoutOrderStatusColumn: IDataTableColumn<TCheckoutOrder> = {
  id: 'status',
  label: 'Status',
  render(id, value, row) {
    const colorConfig =
      checkoutOrderStatusColorMap[value] || checkoutOrderStatusColorMap.default
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Box>
          <Chip
            label={Convertions.checkoutOrderStatusToString(value)}
            size="small"
            sx={{
              backgroundColor: colorConfig.backgroundColor,
              color: colorConfig.color,
            }}
          />
        </Box>
        {row.inAssurance === true && (
          <HtmlTooltip
            title={<Typography>Venda em período de garantia</Typography>}
          >
            <img
              src="/assets/icons/lock/lock.svg"
              alt="icon-sell-type"
              style={{
                backgroundColor: '#FBF3E2',
                width: '24px',
                height: '24px',
              }}
            />
          </HtmlTooltip>
        )}
      </Stack>
    )
  },
}

export const checkoutOrderStatusTimeColumn: IDataTableColumn<
  TCheckoutOrder | TLostSale
> = {
  id: 'status',
  label: 'Status',
  render(id, value, row) {
    const isLostSale = !(row as TCheckoutOrder).valueProductType
    const labelDays = `${!isLostSale ? 'Aberto' : ''} ${OrderFunctions.getDiffDaysDescription(row.createdDate)}`

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Box>
          <Chip
            label={
              <Typography fontSize="12px" lineHeight="16px" fontWeight={500}>
                {labelDays}
              </Typography>
            }
            size="small"
            sx={{
              backgroundColor: '#FBF3E2',
              color: '#887100',
            }}
          />
        </Box>
      </Stack>
    )
  },
}

export const checkoutOrderPaymentMethodColumn: IDataTableColumn<TCheckoutOrder> =
  {
    id: 'paymentyMethod',
    label: 'Forma de Pagamento',
    render(id, value, row) {
      return (
        <Stack direction="row" spacing={1} alignItems={'center'}>
          <MethodTypeSmallIcon methodType={value} />
          <Typography
            fontSize="14px"
            lineHeight="18px"
            color="#1C52BD"
            fontWeight={500}
          >
            {Convertions.paymentMethodToString(value)}
          </Typography>
        </Stack>
      )
    },
  }

export const checkoutOrderPaymentAndCommissionColumns: IDataTableColumn<TCheckoutOrder>[] =
  [
    checkoutOrderPaymentMethodColumn,
    {
      id: 'value',
      label: 'Comissão',
      align: 'right',
      sx: { fontWeight: 500 },
      render(id, value, row) {
        const formatted = NumberFunctions.formatMoneyDefault(
          value,
          row.currency === 'usd' ? 'usd' : 'brl'
        )
        return (
          <DataTableCellTypography sx={{ fontWeight: 500 }}>
            {row.currency === 'usd' ? `🇺🇸 ${formatted}` : formatted}
          </DataTableCellTypography>
        )
      },
    },
  ]

export const CheckoutOrderLinkColumn = ({
  setCopied,
  onDownloadBoleto,
  onCopyPix,
}: ICheckoutOrderOpenedDataTableColumnsProps): IDataTableColumn<TCheckoutOrder> => ({
  id: 'codeId',
  label: 'Checkout',
  minWidth: 60,
  render(id, value, row) {
    const pathAffiliated =
      row.affiliatedCode && row.affiliatedCode !== ''
        ? `?afid=${row.affiliatedCode}`
        : ''
    const url = `${env.PAY_HOST}/c/${value}${pathAffiliated}`
    return (
      <Stack direction="row" spacing={1}>
        <HtmlTooltip
          title={
            row.itemTypeSale === TItemTypeSaleEnum.Subscription
              ? 'Em caso de assinatura a gestão de clientes é feita pelo sistema de assinaturas.'
              : 'Esse link trás todas as informações do usuário no checkout, aumentando a taxa de recuperação.'
          }
        >
          <IconButton
            onClick={async (event) => {
              event.stopPropagation()
              if (row.itemTypeSale === TItemTypeSaleEnum.Subscription) return
              await Hosts.copyTextToClipboard(url)
              setCopied(true)
            }}
            sx={{
              cursor:
                row.itemTypeSale === TItemTypeSaleEnum.Subscription
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            <img src="/assets/icons/actions/link.svg" alt="link" />
          </IconButton>
        </HtmlTooltip>
        {row.paymentyMethod === 'boleto' ? (
          <Button
            variant="text"
            size="small"
            startIcon={<GetAppIcon />}
            sx={{ textTransform: 'none' }}
            onClick={(event) => {
              event.stopPropagation()
              onDownloadBoleto(row.id ?? '', row.orderChargeId ?? '')
            }}
          >
            <Typography
              fontSize="14px"
              lineHeight="18px"
              color="#1C52BD"
              fontWeight={500}
            >
              Baixar Boleto
            </Typography>
          </Button>
        ) : row.paymentyMethod === 'pix' ? (
          <Button
            variant="text"
            size="small"
            startIcon={<img src={IconCopy} alt="copy" />}
            sx={{ textTransform: 'none' }}
            onClick={(event) => {
              event.stopPropagation()
              onCopyPix(row.id ?? '', row.orderChargeId ?? '')
            }}
          >
            <Typography
              fontSize="14px"
              lineHeight="18px"
              color="#1C52BD"
              fontWeight={500}
            >
              Copiar PIX
            </Typography>
          </Button>
        ) : null}
      </Stack>
    )
  },
})

export const CheckoutOrderCustomerWhatsAppColumn = ({
  execute,
}: {
  execute: () => void
}): IDataTableColumn<TCheckoutOrder> => {
  return {
    id: 'phone',
    label: 'Ação',
    minWidth: 60,
    render(id, value, row) {
      let text = `Olá ${row.name},\nObrigado por comprar ${row.productName}`

      if (row.status !== 'paid') {
        const pathAffiliated =
          row.affiliatedCode && row.affiliatedCode !== ''
            ? `?afid=${row.affiliatedCode}`
            : ''
        const url = `${env.PAY_HOST}/c/${row.codeId}${pathAffiliated}`
        text = url
      }

      return (
        <IconButton
          onClick={async (event) => {
            event.stopPropagation()
            const waLink = linkWhatsApp(value, row.phoneCountryCode, row.countryCode)
            if (waLink) {
              Hosts.openNewBlank(`${waLink}?text=${encodeURIComponent(text)}`)
            }

            if (!row.reachedOutForAbandonedOrder) {
              const resp = await OrderController.reachedOutForAbandonedOrder({
                orderId: row.id ?? '',
              })
              if (resp.success) {
                execute()
              }
            }
          }}
        >
          <img src="/assets/icons/actions/whatsapp.svg" alt="link" />
        </IconButton>
      )
    },
  }
}
